import { Description as DialogDescription, Title as DialogTitle } from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import * as Collapsible from '@radix-ui/react-collapsible';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
dayjs.extend(utc);
dayjs.extend(timezone);

import React, { useState } from 'react';

import { Dialog, Input, FormError } from '$partials/ui';
import { SimpleImage } from '$partials/media';
import { useLocale } from '$contexts/locale';
import ModuleContainer from '$partials/modules/container.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ConversationBookingForm}
 */
const ConversationBookingForm = ({
    title,
    start,
    end
}) => {
    const { translate: T } = useLocale();

    const [message, setMessage] = useState();

    const {
        register,
        handleSubmit,
        reset,
        formState: {
            errors
        }
    } = useForm();

    const handleNewsletterSubmit = async (formData) => {
        let slotName = title;
        slotName += ` | ${dayjs(start).tz('Europe/Paris', false).format('DD/MM/YYYY H:mm') }`;
        if (end) {
            slotName += ` — ${dayjs(end).tz('Europe/Paris', false).format('H:mm')}`;
        }

        const response = await fetch('/api/crm/v1/form', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                formId: 'd0abad54-b5c9-4320-bbce-237d184fc2bf',
                fields: {
                    email: formData.emailAddress,
                    firstname: formData.firstName,
                    lastname: formData.lastName,
                    title: formData.title,
                    company: formData.company,
                    jobtitle: formData.jobTitle,
                    'requested_mipim_conversation_slot': slotName
                },
                context: {
                    pageUri: window ? window.location.hostname + window.location.pathname : undefined,
                    pageName: 'Ion Riva'
                }
            })
        });

        const result = await response.json();

        if (result.status !== 200) {
            setMessage(result?.message || T('[FORM_ERROR_MESSAGE]'));
        } else {

            setMessage(T('[CONVERSATION_BOOKING_FORM_THANKYOU]'));
            reset();
        }
    };

    return (
        <form onSubmit={handleSubmit(handleNewsletterSubmit)}>
            <div className='w-full flex flex-col gap-8'>
                <div className='flex gap-8'>
                    <div className='w-1/2'>
                        <div className='rounded bg-offwhite px-12'>
                            <Input
                                {...register('title', { required: T('[FORM_FIELD_TITLE_IS_REQUIRED]') })}
                                id='title'
                                label={T('[FORM_FIELD_TITLE]')} />
                        </div>

                        {errors.title && (
                            <FormError error={errors.title} />
                        )}
                    </div>
                </div>

                <div className='flex gap-8'>
                    <div className='w-1/2'>
                        <div className='rounded bg-offwhite px-12'>
                            <Input
                                {...register('firstName', { required: T('[FORM_FIELD_FIRST_NAME_IS_REQUIRED]') })}
                                id='firstName'
                                label={T('[FORM_FIELD_FIRST_NAME]')} />
                        </div>

                        {errors.firstName && (
                            <FormError error={errors.firstName} />
                        )}
                    </div>

                    <div className='w-1/2'>
                        <div className='rounded bg-offwhite px-12'>
                            <Input
                                {...register('lastName', { required: T('[FORM_FIELD_LAST_NAME_IS_REQUIRED]') })}
                                id='lastName'
                                label={T('[FORM_FIELD_LAST_NAME]')} />
                        </div>

                        {errors.lastName && (
                            <FormError error={errors.lastName} />
                        )}
                    </div>
                </div>

                <div className='w-full'>
                    <div className='rounded bg-offwhite px-12'>
                        <Input
                            {...register('emailAddress', { required: T('[FORM_FIELD_EMAIL_IS_REQUIRED]') })}
                            id='emailAddress'
                            label={T('[FORM_FIELD_EMAIL]')}
                            type='email' />
                    </div>

                    {errors.emailAddress && (
                        <FormError error={errors.emailAddress} />
                    )}
                </div>

                <div className='w-full'>
                    <div className='rounded bg-offwhite px-12'>
                        <Input
                            {...register('company', { required: T('[FORM_FIELD_COMPANY_IS_REQUIRED]') })}
                            id='company'
                            label={T('[FORM_FIELD_COMPANY]')} />
                    </div>

                    {errors.emailAddress && (
                        <FormError error={errors.company} />
                    )}
                </div>

                <div className='w-full'>
                    <div className='rounded bg-offwhite px-12'>
                        <Input
                            {...register('jobTitle', { required: T('[FORM_FIELD_JOB_TITLE_IS_REQUIRED]') })}
                            id='jobTitle'
                            label={T('[FORM_FIELD_JOB_TITLE]')} />
                    </div>

                    {errors.emailAddress && (
                        <FormError error={errors.jobTitle} />
                    )}
                </div>

                <div className='flex gap-8 justify-end items-center'>
                    {message &&
                        <div className='w-1/2 px-12 type-style-tiny'>
                            {message}
                        </div>
                    }

                    <div className='w-1/2'>
                        <button
                            className='flex justify-between items-center bg-purple text-offwhite rounded w-full h-button-height px-12 z-10'
                            type='submit'>
                            <span>
                                {T('[CONVERSATION_BOOKING_FORM_SUBMIT]')}
                            </span>
                            <span>
                                →
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').ConversationBookingItem}
 */
const ConversationBookingItem = ({
    end,
    keynoteSpeaker,
    spacesAvailable,
    start,
    title
}) => {
    const { translate: T } = useLocale();

    const [modalOpen, setModalOpen] = useState(false);
    const [collapsibleOpen, setCollapsibleOpen] = useState(false);

    return (
        <>
            <Collapsible.Root
                onOpenChange={setCollapsibleOpen}
                open={collapsibleOpen}>
                <div className={`expand-interaction flex flex-wrap sm:flex-nowrap items-start w-full gap-8${collapsibleOpen ? ' sm:bg-yellow' : ''}`}>
                    <div className={`flex w-full sm:w-8/12 min-h-button-height ${collapsibleOpen ? ' bg-stone sm:bg-yellow' : 'bg-stone'} rounded items-center py-10 px-6`}>
                        <h4 className='leading-[22px]'>{title}</h4>
                    </div>

                    <div className={`flex sm:w-2/12 h-button-height ${collapsibleOpen ? ' bg-stone sm:bg-yellow' : 'bg-stone'} rounded items-center px-6 truncate min-w-[100px] sm:min-w-[auto]`}>
                        {dayjs(start).tz('Europe/Paris', false).format('H:mm')}
                        {end && <span>—</span>}
                        {dayjs(end).tz('Europe/Paris', false).format('H:mm')}
                    </div>

                    <div className='sm:w-2/12 h-button-height rounded items-center'>
                        {spacesAvailable &&
                            <>
                                <button
                                    className='w-full h-button-height flex basis-0 bg-stone items-center justify-between rounded px-6 min-w-[100px] sm:hidden'
                                    onClick={() => setModalOpen(true)}
                                    type='button'>
                                    <span className='text-purple'>
                                        {T('[CONVERSATION_BOOKING_OPEN]')}
                                    </span>
                                    <span>
                                        →
                                    </span>
                                </button>

                                <Collapsible.Trigger asChild>
                                    <button
                                        className={`expand-interaction__action group/button w-full h-button-height basis-0 items-center justify-between bg-stone hover:bg-purple hover:text-offwhite transition-colors rounded px-6 hidden sm:flex${collapsibleOpen ? ' !justify-end !bg-yellow hover:!text-black hover:!bg-yellow' : ''}`}
                                        type='button'>
                                        {!collapsibleOpen &&
                                            <>
                                                <span className='text-purple group-hover/button:text-offwhite'>
                                                    {T('[CONVERSATION_BOOKING_OPEN]')}
                                                </span>
                                                <span className='group-hover/button:translate-x-[4px] transition-transform'>
                                                    →
                                                </span>
                                            </>
                                        }
                                        {collapsibleOpen &&
                                            <>
                                                ✕
                                            </>
                                        }
                                    </button>
                                </Collapsible.Trigger>
                            </>
                        }

                        {!spacesAvailable &&
                            <div className='w-full h-button-height flex basis-0 bg-stone items-center justify-between rounded px-6 min-w-[100px] sm:min-w-[auto]'>
                                {T('[CONVERSATION_BOOKING_FULL]')}
                            </div>
                        }
                    </div>
                </div>

                <Collapsible.Content className='hidden sm:block w-full bg-yellow'>
                    {(keynoteSpeaker && collapsibleOpen) &&
                        <p className='relative z-10 text-purple px-6 pb-6 -mt-button-baseline'>
                            {T('[KEYNOTE_SPEAKER]')}: {keynoteSpeaker}
                        </p>
                    }

                    <div className='flex'>
                        <div
                            className='w-2/8 p-6 type-style-tiny'
                            dangerouslySetInnerHTML={{__html: T('[CONVERSATION_NOTES]', null, 'RichText')}}>
                        </div>

                        <div className='w-6/8 p-6'>
                            <ConversationBookingForm
                                end={end}
                                start={start}
                                title={title} />
                        </div>
                    </div>
                </Collapsible.Content>
            </Collapsible.Root>

            <Dialog
                className='conversation-dialog type-style-body'
                onOpenChange={(open) => setModalOpen(open)}
                open={modalOpen}>
                <div className='bg-yellow rounded px-12 py-20'>
                    <div className='mb-20'>
                        <DialogTitle>
                            {title}
                        </DialogTitle>
                        {(keynoteSpeaker) &&
                            <p className='text-purple'>
                                Keynote speaker: {keynoteSpeaker}
                            </p>
                        }
                    </div>
                    <DialogDescription className='sr-only'>
                        Complete form to apply to attend this conversation at MIPIM
                    </DialogDescription>

                    <div
                        className='type-style-tiny mb-20'
                        dangerouslySetInnerHTML={{ __html: T('[CONVERSATION_NOTES]', null, 'RichText') }} />

                    <ConversationBookingForm
                        end={end}
                        start={start}
                        title={title} />
                </div>
            </Dialog>
        </>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').ConversationBookingBlock}
 */
const ConversationBookingBlock = ({
    conversations,
    ctaLabel,
    ctaLink,
    heading,
    media,
    text,
}) => {
    let dates = {};

    conversations?.forEach((conversation) => {
        const date = conversation.start.slice(0, 10);
        if (!dates[date]) {
            dates[date] = [];
        }
        dates[date].push(conversation);
    });

    return (
        <ModuleContainer
            ctaLabel={ctaLabel}
            ctaLink={ctaLink}
            heading={heading}
            id='booking'
            text={text}>
            {media &&
                <div className='pb-30'>
                    <div className='overflow-hidden rounded'>
                        {media?.image &&
                            <SimpleImage
                                {...media.image}
                                className='w-full'
                                style={{ aspectRatio: `${media.image.width}/${media.image.height}` }} />
                        }
                        {media?.video &&
                            <video
                                autoPlay={true}
                                className='w-full pointer-events-none'
                                loop={true}
                                muted={true}
                                playsInline={true}
                                poster={media.video.poster}
                                src={media.video.src}
                                style={{ aspectRatio: `${media.video.width}/${media.video.height}` }} />
                        }
                    </div>
                </div>
            }

            <ul className='pb-30'>
                {Object.keys(dates).sort().map((date, i) => {
                    return (
                        <li
                            className='flex flex-wrap border-t border-black py-8 mb-30'
                            key={i}>
                            <div className='w-full md:w-2/8 mb-12 md:mb-0 pt-button-baseline'>
                                <h3>
                                    {dayjs(date).tz('Europe/Paris', false).format('dddd D MMMM')}
                                </h3>
                            </div>

                            <ul className='flex flex-col gap-8 w-full md:w-6/8'>
                                {dates[date].map((conversation, j) => {
                                    return (
                                        <li key={j}>
                                            <ConversationBookingItem {...conversation} />
                                        </li>
                                    );
                                })}
                            </ul>
                        </li>
                    );
                })}
            </ul>
        </ModuleContainer>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ConversationBookingBlock;
